import clsx from "clsx";
import ScrollerPanel from "components/ScrollerPanel";
import {
  ChevronDown,
  ChevronRight,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import {
  arrayOf,
  bool,
  func,
  number,
  oneOfType,
  shape,
  string,
} from "prop-types";
import { useEffect, useRef } from "react";
import isNullish from "utils/isNullish";

import ItemStepper from "../../../components/ItemStepper";
import RequirementsBadge from "./components/RequirementsBadge";
import styles from "./CoverageSectionsPane.module.scss";

const SCROLL_TO = { block: "nearest", behavior: "smooth" };

const CoverageSectionsPane = ({
  rows,
  activeSectionId = null,
  collapsedIds,
  isNarrow = false,
  onSelectSection,
  onStepUpdate,
  onToggleSection,
  onToggleNarrow,
  traceStepCount = 0,
  traceStepIndex = 0,
}) => {
  const ToggleIcon = isNarrow ? PanelLeftOpen : PanelLeftClose;
  const activeRowRef = useRef(null);

  useEffect(() => {
    if (!isNullish(activeRowRef.current)) {
      activeRowRef.current.scrollIntoView(SCROLL_TO);
    }
  }, [activeSectionId]);

  return (
    <div className={styles.pane} data-testid="coverage-sections-pane">
      <div className={styles.header}>
        <span className={styles.heading}>Outline Sections</span>
        <button
          type="button"
          className={styles.iconButton}
          aria-label={
            isNarrow ? "Widen Outline Sections" : "Narrow Outline Sections"
          }
          onClick={onToggleNarrow}
        >
          <ToggleIcon size={16} aria-hidden />
        </button>
      </div>

      <ScrollerPanel className={styles.scroll}>
        {rows.map((row) => {
          const isActive = row.id === activeSectionId;
          const isCollapsed = collapsedIds.includes(row.id);
          const ExpandIcon = isCollapsed ? ChevronRight : ChevronDown;
          const showStepper = isActive && traceStepCount > 1;
          return (
            <div
              key={row.id}
              className={clsx(styles.row, isActive && styles.rowActive)}
              ref={isActive ? activeRowRef : undefined}
              style={{ "--coverage-indent": row.level - 1 }}
            >
              {row.hasChildren ? (
                <button
                  type="button"
                  className={styles.expander}
                  aria-label={`${isCollapsed ? "Expand" : "Collapse"} ${row.title}`}
                  aria-expanded={!isCollapsed}
                  onClick={() => onToggleSection(row.id)}
                >
                  <ExpandIcon size={14} aria-hidden />
                </button>
              ) : (
                <span className={styles.expanderSpacer} />
              )}

              <button
                type="button"
                className={styles.rowButton}
                aria-label={row.title}
                aria-pressed={isActive}
                onClick={() => onSelectSection(row.id)}
              >
                <span className={styles.levelBadge}>L{row.level}</span>
                <span
                  className={clsx(
                    styles.title,
                    row.level <= 2 && styles.titleStrong
                  )}
                  title={row.title}
                >
                  {row.title}
                </span>
                {!showStepper && row.requirementCount > 0 && (
                  <RequirementsBadge
                    length={row.requirementCount}
                    title={row.title}
                  />
                )}
              </button>

              {showStepper && (
                <ItemStepper
                  canLoop={false}
                  className={styles.stepper}
                  current={traceStepIndex + 1}
                  nextLabel="Next Traced Paragraph"
                  onUpdate={onStepUpdate}
                  previousLabel="Previous Traced Paragraph"
                  total={traceStepCount}
                />
              )}
            </div>
          );
        })}
      </ScrollerPanel>
    </div>
  );
};

CoverageSectionsPane.propTypes = {
  activeSectionId: oneOfType([number, string]),
  collapsedIds: arrayOf(oneOfType([number, string])).isRequired,
  isNarrow: bool,
  onSelectSection: func.isRequired,
  onStepUpdate: func.isRequired,
  onToggleNarrow: func.isRequired,
  onToggleSection: func.isRequired,
  rows: arrayOf(
    shape({
      hasChildren: bool,
      id: oneOfType([number, string]),
      level: number,
      requirementCount: number,
      title: string,
    })
  ).isRequired,
  traceStepCount: number,
  traceStepIndex: number,
};

export default CoverageSectionsPane;
